import { ButtonActor, Card, Spacer, Subtitle, Title } from 'components';
import {
  clearCalculatorFromSession,
  setDefaultCalculatorFromSession,
} from '../../utils/constantsSession';
import { Frame } from 'components/organisms';
import { getAmountAndTermCalculator } from '../../hooks/useApplicationSummary';
import { useErrorManager } from 'InternalLibrary';
import classNames from 'classnames';
import layout from 'scss/layout.module.scss';
import styles from './Welcome.module.scss';
import useClient from '../../hooks/useClient';
import useClientConstraints from '../../hooks/useClientConstraints';
import useClientOffer from '../../hooks/useClientOffer';

import { debounce } from 'lodash';
import { Response as GetApplication } from '../../api/typegen/client/application/get';
import { Response as GetClient } from '../../api/typegen/client/get';
import { Response as GetOffer } from '../../api/typegen/application/first-loan-offer/product-number/{productNumber}/get';
import { useGetClientApplication, usePutApplication } from '../../api/atoms';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';

import { App } from '../../App';
import { AppRegistrationSection } from '../../AppRegistrationSection';
import { frameConfig } from '../../AppConfig';
import { getFetchHeaders } from '../../utils/fetchHeaders';
import { routes } from '../../routes';
import { useMaybeCreateApplication } from '../../hooks/useMaybeCreateApplication';

import { useWebcode } from '../../hooks/useWebcode';
import CreditLimitWarningBox from '../../components/CreditLimitWarningBox/CreditLimitWarningBox';
import Loader from '../../components/Loader/Loader';
import useIovation, { ioBlackBoxAtom } from '../../hooks/useIovation';

import { Calculator } from 'components/molecules/Calculator/Calculator';
import { format } from 'date-fns';
import { getScreenTranslation } from '../../utils/translations';
import { Table } from 'components/molecules/Table/Table';

export default function Welcome() {
  const i18Next = useTranslation();
  const { data: offerData, fetch: fetchOffer } = useClientOffer();
  const navigate = useNavigate();
  const { client, fetching: fetchingClient } = useClient();
  const {
    data: applicationData,
    fetch: fetchGetApplication,
    fetching: fetchingApplication,
  } = useGetClientApplication();
  const { data: constraintsData, fetching: fetchingConstraints } =
    useClientConstraints();
  const { fetch: fetchPutClientApplication } = usePutApplication();
  const apiAffiliateEventQualified = App.useApi(
    'affiliate-events/qualified',
    'POST',
  );

  const { isWebCodeRequired } = useWebcode();
  const { maybeCreateApplication } = useMaybeCreateApplication();

  const [warningBoxVisible, setWarningBoxVisible] = useState(false);
  const [loadingNewApplication, setLoadingNewApplication] = useState(false);
  const { setError } = useErrorManager();

  const ioBlackBox = useRecoilValue(ioBlackBoxAtom);
  const calculatorData = getAmountAndTermCalculator({
    constraints: constraintsData,
    application: applicationData,
  });

  useIovation();

  useEffect(() => {
    clearCalculatorFromSession();
  }, []);

  const debouncedFetchOffer = useCallback(
    debounce(async (amount, term) => {
      fetchOffer({
        pathParameters: { productNumber: App.config.productNumber },
        queryParameters: {
          amount: amount,
          term: term,
        },
      });
      setDefaultCalculatorFromSession(amount, term);
    }, 500),
    [],
  );

  const handleChange = (key: string, value: number) => {
    if (key === 'amount') {
      debouncedFetchOffer(value, calculatorData.term);
    }
    if (key === 'days') {
      debouncedFetchOffer(calculatorData.amount, value);
    }
  };

  const handleSumbitExistingApplication = useCallback(
    async ({
      application,
      client,
    }: {
      client: GetClient | null | undefined;
      application: GetApplication;
    }) => {
      const {
        bankAccount,
        hasAttachments,
        firstName,
        lastName,
        dateOfBirth,
        actualAddress,
        employer,
      } = client || {};
      let isPhoneVerificationRequired = true;
      try {
        const response = await isWebCodeRequired();
        if (response !== undefined) isPhoneVerificationRequired = response;
      } catch {
        setError('login_unavailable');
        return;
      }

      // If client has opened applicaiton, we should already know the name so this shouldn't be needed

      if (!firstName || !lastName || !dateOfBirth) {
        navigate(routes.FirstLoan_PersonalData);
        return;
      }

      if (!actualAddress?.fullAddress) {
        navigate(routes.FirstLoan_Address);
        return;
      }

      if (!employer?.employmentStatus) {
        navigate(routes.FirstLoan_Income);
        return;
      }

      if (isPhoneVerificationRequired) {
        navigate(routes.FirstLoan_PhoneVerification);
        return;
      }

      if (client?.status === 'IDENTIFIED' || (hasAttachments && bankAccount)) {
        navigate(routes.FirstLoan_Manual_Upload_Pending);
        return;
      }

      if (!application.confirmed) {
        navigate(routes.FirstLoan_PhoneVerification_Check);
        return;
      } else if (client?.isRegisteredByAffiliate) {
        await apiAffiliateEventQualified.fetch({
          headers: getFetchHeaders(),
        });
      }

      navigate(routes.FirstLoan_OnlineBank_Verify);
    },
    [navigate],
  );

  const handleSumbitNewApplication = useCallback(
    //TODO: Export CalculatorData type from lib and replace this horrifying "any"
    async ({
      data,
      client,
      offer: { newPrincipal },
    }: {
      data: any;
      client: GetClient | null | undefined;
      offer: GetOffer;
    }) => {
      const {
        firstName,
        lastName,
        dateOfBirth,
        actualAddress,
        employer,
        creditLimit,
      } = client || {};
      try {
        setLoadingNewApplication(true);
        if (!firstName || !lastName || !dateOfBirth) {
          navigate(routes.FirstLoan_PersonalData);
          return;
        }

        if (newPrincipal && creditLimit && creditLimit < newPrincipal) {
          setWarningBoxVisible(true);
          await debouncedFetchOffer(creditLimit, calculatorData.term);
          return;
        }

        if (
          applicationData?.status &&
          !applicationData?.closed &&
          !applicationData?.confirmed
        ) {
          await fetchPutClientApplication({
            body: {
              ioBlackBox,
            },
          });
          await fetchGetApplication();
        }

        if (!actualAddress?.fullAddress) {
          navigate(routes.FirstLoan_Address);
          return;
        }

        if (!employer?.employmentStatus) {
          navigate(routes.FirstLoan_Income);
          return;
        }

        const isWebCodeNeeded = await isWebCodeRequired();

        let nextRoute = routes.FirstLoan_OnlineBank_Verify;

        if (isWebCodeNeeded) {
          if (applicationData?.status === 'OPEN') {
            navigate(routes.FirstLoan_PhoneVerification);
            return;
          } else {
            nextRoute = routes.FirstLoan_PhoneVerification;
          }
        }

        // TODO: What if user is < 21?
        await maybeCreateApplication({
          amount: data?.amount,
          term: data?.term,
          nextRoute,
          confirmApplication: true,
        });

        // In case there has not been any redirection
        if (window.location.pathname === routes.FirstLoan_Welcome) {
          navigate(routes.FirstLoan_OnlineBank_Verify);
        }
      } catch {
      } finally {
        setLoadingNewApplication(false);
      }
    },
    [applicationData, maybeCreateApplication, navigate],
  );

  if (!applicationData || !client || !constraintsData || !offerData) {
    return <Loader />;
  }

  const content = getScreenTranslation('welcome', i18Next);

  const isApplicationNew =
    loadingNewApplication ||
    !applicationData?.status ||
    applicationData?.status === 'CLOSED';

  if (isApplicationNew) {
    const calculatorProps = {
      content: {
        title: i18Next.t('common:calculator.title'),
        confirmChanges: i18Next.t('common:calculator.confirmChanges'),
        openLoanInformationModal: i18Next.t(
          'common:calculator.openLoanInformationModal',
        ),
        amountTitle: i18Next.t('registration_header:title_amount'),
        daysTitle: i18Next.t('registration_header:title_days'),
        amountUnit: i18Next.t('common:currencySymbol'),
        daysUnit: i18Next.t('common:days'),
        swiperQuantity: i18Next.t('common:calculator.swiperQuantity'),
        swiperTerm: i18Next.t('common:calculator.swiperTerm'),
      },
      onChange: handleChange,
      onSubmit: (data: any) =>
        handleSumbitNewApplication({
          data,
          client,
          offer: offerData,
        }),
      constraints: {
        amount: {
          max:
            warningBoxVisible || !!client?.dateOfBirth
              ? client?.creditLimit ?? 300
              : 300,
          min: constraintsData?.amountInterval?.min ?? 50,
          step: constraintsData?.amountInterval?.step ?? 10,
        },
        term: {
          max: constraintsData?.termInterval?.max ?? 30,
          min: constraintsData?.termInterval?.min ?? 7,
          step: constraintsData?.termInterval?.step ?? 1,
        },
      },
      data: {
        amount: calculatorData.amount,
        days: calculatorData.term,
      },
    };

    return (
      <Frame
        {...frameConfig}
        hideLogout
        sectionID={AppRegistrationSection.Intro}
        hideEditButton
        bodyWrapperClassName={classNames(
          layout['frame-x-compact'],
          layout['width-lg'],
          styles.frameBodyWrapper,
        )}
      >
        <Title text={content.title} className={styles.title} />

        <Calculator {...calculatorProps}>
          <Card
            className={classNames(
              layout['frame-x-compact'],
              layout['card-compact'],
              styles.card,
            )}
          >
            <span className={styles.calculatorTitle}>
              {calculatorProps.content.title}
            </span>
            <Calculator.Form className={styles.calculatorForm} />
            {warningBoxVisible && (
              <div className={styles.componentWrapper}>
                <CreditLimitWarningBox />
              </div>
            )}
          </Card>
          <div className={styles.continueButtonWrapper}>
            <Calculator.SubmitButton
              className={classNames(layout['width-sm'], styles.continueButton)}
            />
          </div>
        </Calculator>
      </Frame>
    );
  }

  const tableItems = [
    {
      label: i18Next.t('welcome:table.row1'),
      value: `${offerData?.newPrincipal}${i18Next.t('common:currencySymbol')}`,
    },
    {
      label: i18Next.t('welcome:table.row2'),
      value: `${offerData?.totalInterest}${i18Next.t('common:currencySymbol')}`,
    },
    {
      label: i18Next.t('welcome:table.row3'),
      value: `${offerData?.totalRepayableAmount}${i18Next.t(
        'common:currencySymbol',
      )}`,
    },
    {
      label: i18Next.t('welcome:table.row4'),
      value: format(new Date(offerData?.firstPaymentDate || ''), 'dd/MM/yyyy'),
    },
  ];

  return (
    <Frame
      {...frameConfig}
      hideEditButton
      sectionID={AppRegistrationSection.Intro}
      bodyWrapperClassName={classNames(
        layout['frame-x-compact'],
        layout['width-lg'],
      )}
    >
      <Spacer marginBottom="20px">
        <Title text={content.title} />
      </Spacer>

      <Subtitle text={content.subtitle} textClassName={styles.subtitle} />

      <Card className={layout['card-compact']}>
        <Spacer padding="24px 30px">
          <Table items={tableItems} title={content.tableTitle} />
        </Spacer>
      </Card>

      <div
        className={classNames(
          layout['frame-x-padding'],
          styles['link-button-wrapper'],
        )}
      >
        <ButtonActor
          buttonProps={{
            onClick: async () =>
              handleSumbitExistingApplication({
                client,
                application: applicationData,
              }),
            loading:
              fetchingClient || fetchingApplication || fetchingConstraints,
          }}
          className={classNames(layout['width-sm'], styles.continueButton)}
        >
          {content.continueButtonText}
        </ButtonActor>
      </div>
    </Frame>
  );
}
